import { captureException } from '@sentry/browser'
import { useCallback, useEffect, useRef, useState } from 'react'

import { QuickGuiddeType, QuizCtaType, QuizPassingScore, QuizResult, QuizStatus, VideoPlayer } from 'app/types'

import { QuizAnalytics } from './quiz-analytics'
import { areRequiredQuizzesPassed, getQuizStatus, isQuizComplete } from './quiz-helpers'
import { useSubmitQuizAttempt } from './use-submit-quiz-attempt'
import { useVisibleQuizzes } from './use-visible-quizzes'

const END_EPSILON = 0.5

// The base embed VideoPlayer does not expose play/seek; quiz playback needs them. When wiring the
// overlay, extend the real player (or adapt it) to satisfy this shape.
export type QuizPlaybackPlayer = VideoPlayer & {
    play: () => void
    seek: (_time: number) => void
}

type CompletedQuiz = {
    status: QuizStatus | 'skipped'
    correctCount: number
    totalQuestions: number
    passingScore: QuizPassingScore
}

export type QuizView = { type: 'idle' } | { type: 'quiz'; quiz: QuizCtaType } | { type: 'ending' }

export const useQuizPlayback = (playbook: QuickGuiddeType, player: QuizPlaybackPlayer | null) => {
    const { submitAttempt } = useSubmitQuizAttempt()
    const { quizzes, hasQuiz, requiredQuizIds, priorPassedIds } = useVisibleQuizzes(playbook)

    const [activeQuiz, setActiveQuiz] = useState<QuizCtaType | null>(null)
    const [completedQuizzes, setCompletedQuizzes] = useState<Record<string, CompletedQuiz>>({})
    const [showEnding, setShowEnding] = useState(false)

    // Refs so the time-update listener registers once per player and still reads fresh state
    const activeQuizRef = useRef(activeQuiz)
    activeQuizRef.current = activeQuiz
    const showEndingRef = useRef(showEnding)
    showEndingRef.current = showEnding
    const completedQuizzesRef = useRef(completedQuizzes)
    completedQuizzesRef.current = completedQuizzes

    // Per-quiz submission counter so analytics can tell first attempts from retakes
    const attemptCountsRef = useRef<Record<string, number>>({})

    const isEndingReady = useCallback(
        (done: Record<string, CompletedQuiz>) => {
            if (!hasQuiz) return false
            const passed = new Set(priorPassedIds)
            Object.entries(done).forEach(([quizId, quiz]) => {
                if (isQuizComplete(quiz.status)) passed.add(quizId)
            })
            return areRequiredQuizzesPassed(requiredQuizIds, passed)
        },
        [hasQuiz, priorPassedIds, requiredQuizIds],
    )

    useEffect(() => {
        if (!player || !hasQuiz) return

        const handleTimeUpdate = () => {
            if (activeQuizRef.current || showEndingRef.current) return

            const currentTime = player.getCurrentTime()

            const reached = quizzes.find(
                entry => !completedQuizzesRef.current[entry.quiz.id] && currentTime >= entry.showAt,
            )
            if (reached) {
                player.pause()
                setActiveQuiz(reached.quiz)
                QuizAnalytics.attemptStarted({
                    playbookId: playbook.id,
                    quizId: reached.quiz.id,
                    questionCount: reached.quiz.questions.length,
                })
                return
            }

            const duration = player.getDuration()
            const videoReachedEnd = duration > 0 && duration - currentTime <= END_EPSILON

            if (videoReachedEnd && isEndingReady(completedQuizzesRef.current)) setShowEnding(true)
        }

        player.addEventListener(player.eventNames.timeUpdate, handleTimeUpdate)
        return () => player.removeEventListener(player.eventNames.timeUpdate, handleTimeUpdate)
    }, [player, quizzes, hasQuiz, isEndingReady, playbook.id])

    // Submit on "See results" — every run, pass or fail, including retakes
    const handleSubmitAttempt = (result: QuizResult) => {
        const quiz = activeQuizRef.current
        if (!quiz) return

        const attemptNumber = (attemptCountsRef.current[quiz.id] ?? 0) + 1
        attemptCountsRef.current[quiz.id] = attemptNumber

        QuizAnalytics.attemptSubmitted({
            playbookId: playbook.id,
            quizId: quiz.id,
            correctCount: result.correctCount,
            totalQuestions: result.totalQuestions,
            isPassed: result.isPassed,
            attemptNumber,
        })

        submitAttempt({
            playbookId: playbook.id,
            quizId: quiz.id,
            passingScore: quiz.passingScore,
            requirePassToComplete: quiz.requirePassToComplete,
            isPassed: result.isPassed,
            answeredQuestions: result.answeredQuestions,
        }).catch(captureException)
    }

    // Mark the quiz as done for this session and resume; show the ending if the video already finished
    const dismissQuiz = (next: Record<string, CompletedQuiz>) => {
        setCompletedQuizzes(next)
        setActiveQuiz(null)

        const duration = player?.getDuration() ?? 0
        const currentTime = player?.getCurrentTime() ?? 0
        const videoFinished = duration > 0 && duration - currentTime <= END_EPSILON

        if (videoFinished && isEndingReady(next)) setShowEnding(true)
        else player?.play()
    }

    const handleContinue = (result: QuizResult) => {
        const quiz = activeQuizRef.current
        if (!quiz) return

        dismissQuiz({
            ...completedQuizzesRef.current,
            [quiz.id]: {
                status: getQuizStatus(quiz, result),
                correctCount: result.correctCount,
                totalQuestions: result.totalQuestions,
                passingScore: quiz.passingScore,
            },
        })
    }

    // Optional quizzes (requirePassToComplete === false): dismiss for the session without submitting an
    // attempt, so it won't re-trigger on seek-back. A fresh session (reload) shows it again.
    const handleSkip = () => {
        const quiz = activeQuizRef.current
        if (!quiz) return

        QuizAnalytics.skipped({ playbookId: playbook.id, quizId: quiz.id })

        dismissQuiz({
            ...completedQuizzesRef.current,
            [quiz.id]: {
                status: 'skipped',
                correctCount: 0,
                totalQuestions: quiz.questions.length,
                passingScore: quiz.passingScore,
            },
        })
    }

    const handleRetake = () => {
        const quiz = activeQuizRef.current
        if (!quiz) return

        QuizAnalytics.retakeClicked({ playbookId: playbook.id, quizId: quiz.id })
    }

    // "I skipped ahead and don't know this yet" — rewind to the start of the content this quiz covers
    // (the previous quiz's showAt, or video start) and replay; the quiz reopens when playback reaches it again
    const handleRewatch = () => {
        const quiz = activeQuizRef.current
        if (!quiz) return

        QuizAnalytics.rewatchClicked({ playbookId: playbook.id, quizId: quiz.id })

        const current = quizzes.find(entry => entry.quiz.id === quiz.id)
        if (!current) return

        const previousShowAt = quizzes
            .filter(entry => entry.showAt < current.showAt)
            .reduce((max, entry) => Math.max(max, entry.showAt), 0)

        player?.seek(previousShowAt)
        setActiveQuiz(null)
        player?.play()
    }

    const reviewGuide = () => {
        QuizAnalytics.reviewGuideClicked({ playbookId: playbook.id })
        setShowEnding(false)
        setCompletedQuizzes({})
        attemptCountsRef.current = {}
        player?.seek(0)
        player?.play()
    }

    const view: QuizView = activeQuiz
        ? { type: 'quiz', quiz: activeQuiz }
        : showEnding
          ? { type: 'ending' }
          : { type: 'idle' }

    return {
        view,
        submitAttempt: handleSubmitAttempt,
        continueGuide: handleContinue,
        rewatch: handleRewatch,
        retake: handleRetake,
        skip: handleSkip,
        reviewGuide,
    }
}
